import type { FC } from "react"

import type { EmailAttachment } from "@productlane/api"
import { trimTrailingEmptyElements } from "@productlane/lib/src/html"
import { IconButton } from "@productlane/ui"
import { EllipsisIcon as Ellipsis } from "@productlane/ui/src/icons/project/ellipsis-icon"

import { EmailHtml } from "@/components/email-html"
import { lruCached } from "@/lib/lru-cache"
import { useMailCollapse } from "./use-mail-collapse"

export { useMailCollapse }

// Trimming is a full cheerio parse, and every timeline render trims every email.
const TRIMMED_CACHE_LIMIT = 64
const trimmedCache = new Map<string, string>()
const trimmedBody = (html: string) =>
	lruCached(trimmedCache, TRIMMED_CACHE_LIMIT, html, () =>
		trimTrailingEmptyElements(html),
	)

interface Props {
	htmlContent: string
	attachments: EmailAttachment[]
	defaultExpanded?: boolean
	authoredInProductlane?: boolean
}

export const CollapsibleMailContent: FC<Props> = ({
	htmlContent,
	attachments,
	defaultExpanded = false,
	authoredInProductlane = false,
}) => {
	const { isCollapsed, toggleCollapse, htmlMailChunks } = useMailCollapse(
		htmlContent,
		attachments,
		defaultExpanded,
	)

	return (
		<>
			{htmlMailChunks[0] && (
				<EmailHtml
					html={trimmedBody(htmlMailChunks[0])}
					authoredInProductlane={authoredInProductlane}
				/>
			)}

			{htmlMailChunks[1] && (
				<div>
					<IconButton
						variant="link"
						className="-ml-2"
						onClick={toggleCollapse}
						Icon={Ellipsis}
					/>
					{!isCollapsed && (
						<EmailHtml
							html={htmlMailChunks[1]}
							authoredInProductlane={authoredInProductlane}
						/>
					)}
				</div>
			)}
		</>
	)
}
